import {
  IMAGING_MODALITY_LABELS,
  parseIsoDay,
  startOfEatDay,
  type ImagingModality,
} from '@wodi/domain';
import {
  IMAGING_EXAM_STATE_LABELS,
  type ImagingExamState,
  type ImagingWorklistState,
} from '@wodi/shared';
import type { TagState } from '@wodi/ui';

// Words and shapes for the imaging screens (docs/plans/phase2-radiology.md §6). States are
// words with a neutral tag shape (ADR 0019); never a colour.

export const WORKLIST_TABS: readonly { state: ImagingWorklistState; label: string; key: string }[] =
  [
    { state: 'requested', label: 'Requested', key: '1' },
    { state: 'scheduled', label: 'Scheduled', key: '2' },
    { state: 'performed', label: 'Performed', key: '3' },
    { state: 'reported', label: 'Reported', key: '4' },
    { state: 'verified', label: 'Verified today', key: '5' },
  ];

export const STATE_SHAPES: Record<ImagingExamState, TagState> = {
  requested: 'pending',
  scheduled: 'pending',
  performed: 'attention',
  reported: 'attention',
  verified: 'done',
  cancelled: 'neutral',
  entered_in_error: 'neutral',
};

export function stateWords(state: ImagingExamState): string {
  return IMAGING_EXAM_STATE_LABELS[state];
}

export function modalityWords(modality: ImagingModality | null): string {
  return modality === null ? 'Modality not set' : IMAGING_MODALITY_LABELS[modality];
}

/** The words shown beside every unverified report (plan R2, TODO(clinical-verify)). */
export const PRELIMINARY = 'Preliminary: not yet verified';

/** "35 min", "2 h 5 min"; a time still ahead reads "Not yet due". */
export function formatWaiting(minutes: number): string {
  if (minutes < 0) return 'Not yet due';
  if (minutes < 60) return `${String(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${String(hours)} h` : `${String(hours)} h ${String(rest)} min`;
}

/**
 * The instant for a day (`YYYY-MM-DD`) and a 24-hour time (`14:05`) in East Africa Time,
 * or null when either doesn't read as one. EAT has no daylight saving.
 */
export function eatInstant(day: string, time: string): Date | null {
  const parsed = parseIsoDay(day);
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time.trim());
  if (!parsed.ok || match === null) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return new Date(startOfEatDay(parsed.value).getTime() + minutes * 60_000);
}
